// #region IMPORTS
import { proxy, ref } from 'valtio';

import { BVHLoader } from '@cartwhl/3d/bvh';
import getPose from '@cartwhl/common/api/getPose';
// import getProject from '@cartwhl/common/api/getProject';
import postPose from '@cartwhl/common/api/postPose';
import postPrepareRenderCanvas from '@cartwhl/common/api/postPrepareRenderCanvas';
import postRenderCanvas from '@cartwhl/common/api/postRenderCanvas';
import postRenderCanvasFeedback from '@cartwhl/common/api/postRenderCanvasFeedback';
import putPose from '@cartwhl/common/api/putPose';
import { GenerationHistoryFromAPI, Project } from '@cartwhl/common/types';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

import PosingSystem from 'components/PoseMode/lib/PosingSystem';
import {
  authStore,
  characterPickerStore,
  navigationStore,
  noticesStore,
  projectStore,
  sceneSettingsStore,
  webSocketStore,
} from 'stores';

import { DEFAULT_POSE_CHARACTER_ID } from './characterPickerStore';

const IS_DEV_ENV = process.env.NODE_ENV === 'development';

// #endregion

// #region SUPPORT ENUMS, TYPES

// Various ways to express Vector3 data
export type Vec3Like = Array<number> | [number, number, number] | Float32Array;

// Define specific IK chains the user can manipulate
export type IKChainType = (typeof IKChainType)[keyof typeof IKChainType];
export const IKChainType = Object.freeze({
  None: 'none',
  LeftArm: 'left_arm',
  LeftLeg: 'left_leg',
  LeftToe: 'left_toe',
  RightArm: 'right_arm',
  RightLeg: 'right_leg',
  RightToe: 'right_toe',
  Pelvis: 'pelvis',
  Spine: 'spine',
  Neck: 'neck',
  Head: 'head',
});

// Various modes available in threejs transformation gizmo
export type GizmoModeType = (typeof GizmoModeType)[keyof typeof GizmoModeType];
export const GizmoModeType = Object.freeze({
  Rotate: 'rotate',
  Translate: 'translate',
  Scale: 'scale',
  zTwist: 'ztwist',
  xTwist: 'xtwist',
  yTwist: 'ytwist',
});

// TODO: Stand in type for Pose object being used in history, Update when forge is ready to replace ossos
type Pose = {
  [key: string]: unknown;
};

// #endregion

// #region CONSTANTS
const MAX_HISTORY_LENGTH = 40;
// #endregion

// #region STORE DEFINITION

export type PosingState = {
  posingSystem: { current: PosingSystem | null }; // valtio ref<>,

  gizmoMode: GizmoModeType; // Control gizmo various modes
  gizmoGlobalRootActive: boolean; // Use global mode when using the gizmo on the hips/root
  activeIKChain: IKChainType; // Know which IK chain is active

  // Render pose prompt & output
  lastRenderPrompt: string;
  renderingInProgress: boolean;
  renderedPoseURL?: string;
  lastCanvasBase64?: string;

  // UI State
  showSkeletonHelper: boolean; // Show/hide skeleton helper

  historyStack: Array<Pose>; // valtio.ref<Array<Ossos.Pose>>([])
  historyUpdatedOn: Date; // Stack uses a Ref, this properly can be used to trigger reactivity for the stack
  historyCursor: number; // Current index of the stack

  // Pose Project Loading
  poseRenderInformation: {
    bvhURL: string;
    reloadKey: number;
  } | null;

  // Track if pose has been adjusted from default
  poseHasBeenAdjusted: boolean;
};

export const state = proxy<PosingState>({
  posingSystem: ref<{ current: PosingSystem | null }>({ current: null }),
  gizmoGlobalRootActive: false,
  gizmoMode: GizmoModeType.Translate,
  activeIKChain: IKChainType.None,

  // Render pose prompt & output
  lastRenderPrompt:
    'A person standing in a t-pose getting measured by a tailor so that they can be fitted in custom formal attire.',
  renderingInProgress: false,
  renderedPoseURL: undefined,
  lastCanvasBase64: undefined,

  // UI State
  showSkeletonHelper: false, // default off
  historyStack: ref<Array<Pose>>([]),
  historyCursor: -1,
  historyUpdatedOn: new Date(),

  // Pose Project Loading
  poseRenderInformation: null,
  poseHasBeenAdjusted: false,
});

export async function init() {
  // TODO: Is there any valid initialization to do here?
  // ProjectStore is responsible for loading the project
  // which leads to loading of the existing pose data.
}

// #endregion

// #region STATE UPDATE HELPERS

export function setLastCanvasBase64(canvasBase64: string) {
  state.lastCanvasBase64 = canvasBase64;
}

export function updateReloadKey() {
  if (!state.poseRenderInformation) {
    return;
  }
  state.poseRenderInformation.reloadKey = Date.now();
}

export function clearActiveIKChain() {
  state.activeIKChain = IKChainType.None;
}
// #endregion

// #region HISTORY FUNCTIONALITY

/** Pass a new item to the history stack */
export function historyPush(itm: Pose) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Apply Constraints on the stack
  const stack = state.historyStack;

  // Clear out items after the cursor position
  if (state.historyCursor < stack.length - 1) {
    stack.length = Math.max(0, state.historyCursor + 1);
  }

  // Remove Elements from the start of the array beyond max
  const maxDiff = Math.max(0, stack.length - MAX_HISTORY_LENGTH + 1);

  if (maxDiff > 0) {
    // Array is inside a valtio.ref & documentation says not to reassign property
    // in typical react fashion as it'll replace the ref with the new array.
    // Need to treat the array as mutable data but .splice() doesn't seem to
    // work as it should, the ref wrapper might be blocking it. Shift() was the
    // only other mutable way to remove items from the start of the array.
    for (let i = 0; i < maxDiff; i++) stack.shift();
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Update History Data
  stack.push(itm);

  state.historyCursor = stack.length - 1;
  state.historyUpdatedOn = new Date();
}

export function historyClear() {
  state.historyStack.length = 0;
  state.historyCursor = -1;
  state.historyUpdatedOn = new Date();
}

export function historyUndo(): Pose | null {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Conditions
  const stack = state.historyStack;
  const prevIdx = state.historyCursor - 1;
  if (stack.length === 0 || prevIdx < 0) {
    return null;
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Get item & update history data
  const itm = stack[prevIdx];

  state.historyUpdatedOn = new Date();
  state.historyCursor = Math.max(0, prevIdx);
  return itm;
}

export function historyRedo(): Pose | null {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Conditions
  const stack = state.historyStack;
  const hCnt = stack.length;
  const nextIdx = state.historyCursor + 1; // Next Index

  if (hCnt === 0 || nextIdx >= hCnt) {
    return null;
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Get item & update history data
  const itm = stack[nextIdx];

  state.historyUpdatedOn = new Date();
  state.historyCursor = Math.min(hCnt, nextIdx);
  return itm;
}

// #endregion

// #region POSE PROJECT LOADING

export async function fetchPoseFromProject(projectData: Project) {
  if (!projectData) {
    throw new Error('No project data received');
  }

  // Extract BVH URL from the project data
  const bvhURL = extractBvhURLFromProject(projectData);

  if (!bvhURL) {
    // Don't throw error - just continue with default pose
  } else {
    const charUploadID =
      sceneSettingsStore.state.characterSettings?.charUploadID;
    const poseJobIDs = projectStore.getJobIDsFromProjectTimeline(projectData);
    if (charUploadID && poseJobIDs.length > 0) {
      characterPickerStore.setActiveCharacterID(charUploadID);
    }
  }
  // Clear undo/redo history when loading a new project
  historyClear();

  // Update the store with the project data
  if (bvhURL) {
    IS_DEV_ENV && console.log('fetchPoseFromProject - pose bvh is available');
    state.poseRenderInformation = {
      bvhURL: bvhURL || '', // Handle null case
      reloadKey: Date.now(),
    };
  }

  // Apply scene settings
  if (projectData.sceneSettings) {
    // console.log('0000--fetchPoseFromProject', projectData.sceneSettings);
    IS_DEV_ENV &&
      console.log(
        'fetchPoseFromProject - resetting scene settings & set project character',
      );
    sceneSettingsStore.reset(projectData.sceneSettings);
  }

  return { success: true, bvhURL };
}

export function extractBvhURLFromProject(projectData: Project): string | null {
  if (projectData.characters) {
    const characters = Object.values(projectData.characters);
    for (const character of characters) {
      if (character.timeline && Array.isArray(character.timeline)) {
        for (const item of character.timeline) {
          if (item.bvhURL) {
            return item.bvhURL;
          }
        }
      }
    }
  }

  return null;
}

export function extractRenderedPoseInformationFromProject(
  projectData: Project,
  generationHistory: GenerationHistoryFromAPI,
): { renderedPngURL?: string; renderPrompt?: string } | undefined {
  const jobIDs = projectStore.getJobIDsFromProjectTimeline(projectData);
  if (generationHistory) {
    for (const generation of generationHistory) {
      if (
        jobIDs.includes(generation.jobID) &&
        'renderedPngURL' in generation &&
        generation.renderedPngURL !== ''
      ) {
        return {
          renderedPngURL: generation.renderedPngURL,
          renderPrompt: generation.renderPrompt,
        };
      }
    }
  }

  return;
}

export function clearCurrentProject() {
  state.poseRenderInformation = null;
  if (state.posingSystem?.current?.poser) {
    state.posingSystem.current.poser.resetPose();
  }

  // Clear undo/redo history when clearing current project
  historyClear();
  resetPoseAdjustmentTracking();

  const currentPath = window.location.pathname;
  navigationStore.navigate(currentPath);
}

/**
 * Comprehensive reset for pose mode - matches mogen resetJobData pattern
 * Resets everything to initial state when starting fresh pose session using  the "Create New Poser" button
 */
export function resetPoseSession() {
  state.poseRenderInformation = null;
  if (state.posingSystem?.current?.poser) {
    state.posingSystem.current.poser.resetPose();
  }
  historyClear();
  resetPoseAdjustmentTracking();
  characterPickerStore.setActiveCharacterID(DEFAULT_POSE_CHARACTER_ID);
  if (characterPickerStore.state.activeCharacter?.meshData.current) {
    characterPickerStore.state.activeCharacter.meshData.current = null;
  }
  sceneSettingsStore.reset();
}

// #endregion

// #region POSE PROJECT CREATION

/**
 * Creates a new pose object when the user adjusts the pose from default T-pose
 * If a jobID is provided, the pose will be created from the jobID via the pose library
 */
export async function createNewPoseObject(
  projectID: string,
  jobID?: string,
  canvasImageBase64?: string,
): Promise<{
  success: boolean;
  projectID?: string;
  error?: string;
}> {
  if (authStore.state.authStatus === 'anonymous') {
    return {
      success: false,
      error: 'You must be logged in to create a pose project!',
    };
  }

  if (
    !webSocketStore.webSocket ||
    !webSocketStore.webSocket.connectionID ||
    !webSocketStore.webSocket.endpoint
  ) {
    return {
      success: false,
      error: 'Websockets are not connected. Try refreshing your page.',
    };
  }

  // Generate BVH content from current pose or from the jobID
  let bvhContent = '';
  let studioPreviewBase64 = null;
  if (jobID) {
    const output = await getDataFromExistingPoseJob(jobID);

    if (output.success) {
      bvhContent = output.bvhContent;
      studioPreviewBase64 = output.studioPreviewBase64;
    }
  } else if (state.posingSystem?.current?.poser) {
    try {
      const poser = state.posingSystem.current.poser;

      bvhContent = poser.exportBVHPose();
    } catch (error) {
      // Fallback to a more complete default pose if generation fails
    }
  }
  const imageBase64 = studioPreviewBase64 || canvasImageBase64 || '';

  // Create the pose within the project
  const poseData = await postPose(projectID, {
    bvhContent,
    canvasImageBase64: imageBase64,
    title: 'New Pose',
    duration: 1,
  });

  if (!poseData.data) {
    return {
      success: false,
      error: 'Failed to create pose. Please try again.',
    };
  }

  return {
    success: true,
  };
}

export async function addPoseToExistingProject(jobID: string) {
  const currentProject = projectStore.state.currentProject;
  if (!currentProject?.data?.projectID) {
    return {
      success: false,
      error: 'No active project to add pose to.',
    };
  }
  const output = await getDataFromExistingPoseJob(jobID);
  if (output.success) {
    const postPoseResponse = await postPose(currentProject.data.projectID, {
      bvhContent: output.bvhContent,
      canvasImageBase64: output.studioPreviewBase64 || '',
      title: output.title,
      duration: 1,
    });
    if (postPoseResponse.error) {
      await handleGenericErrors(postPoseResponse.error);
      return {
        success: false,
        error: 'Failed to add pose to project. Please try again.',
      };
    }
    await projectStore.loadCurrentProject(currentProject.data.projectID);
    const updatedProject = projectStore.state.currentProject.data;
    if (updatedProject) {
      await fetchPoseFromProject(updatedProject);
    }
    // Bump reloadKey to force a reload even if URL is unchanged
    if (state.poseRenderInformation) {
      state.poseRenderInformation.reloadKey = Date.now();
    }
    return { success: true };
  }
}

export async function getDataFromExistingPoseJob(jobID: string) {
  // get the job so that we can pull the bvhURL from the job
  const { data, error: jobError } = await getPose(jobID);
  if (jobError) {
    await handleGenericErrors(jobError);
    return {
      bvhContent: '',
      success: false,
      error: 'Failed to get job data. Please try again.',
      title: '',
    };
  }
  const bvhContentURL = data?.bvhURL || '';
  const bvhContent = await fetch(bvhContentURL).then((res) => res.text());

  const studioPreviewURL = data?.studioPreviewURL || '';

  const studioPreviewBase64 = await fetch(studioPreviewURL)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Remove the data URL prefix to get only the base64 string
            const result = reader.result as string;
            // This will match "data:image/png;base64," or similar
            const base64 = result.replace(/^data:image\/\w+;base64,/, '');
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );

  const title = data?.title || 'New Pose';
  return { success: true, bvhContent, studioPreviewBase64, title };
}

export function markPoseAsAdjusted() {
  state.poseHasBeenAdjusted = true;
}

export function resetPoseAdjustmentTracking() {
  state.poseHasBeenAdjusted = false;
}

export async function updateCurrentProjectPose(
  currentProject: Project,
  canvasImageBase64: string,
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const project = currentProject;
    if (!project?.projectID) {
      return { success: false, error: 'No active project to update.' };
    }

    const poser = state.posingSystem?.current?.poser;
    if (!poser) {
      return { success: false, error: 'Poser is not initialized.' };
    }

    const bvhContent = poser.exportBVHPose();

    const activeCharacterID =
      sceneSettingsStore.state.characterSettings.charUploadID;
    const characterKeys = Object.keys(project.characters || {});
    const objectID =
      characterKeys.find(
        (key) => project.characters[key]?.characterID === activeCharacterID,
      ) || characterKeys[0];

    if (!objectID) {
      return {
        success: false,
        error: 'No character found in project to update',
      };
    }

    const { error } = await putPose(project.projectID, {
      bvhContent,
      canvasImageBase64,
      title: '',
      duration: 1,
      objectID,
    });

    if (error) {
      return { success: false, error: error.message || 'putPose failed' };
    }
    markPoseAsAdjusted();

    return { success: true };
  } catch (e: unknown) {
    return {
      success: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

export async function loadPoseFromJobID(jobID: string): Promise<boolean> {
  const output = await getDataFromExistingPoseJob(jobID);
  if (output.success) {
    const loader = new BVHLoader();
    const bvh = loader.parse(output.bvhContent);

    state.posingSystem.current?.poser.setPoseFromClip(bvh.clip);
    state.posingSystem.current?.fitViewActiveCharacter();
    state.posingSystem.current?.updateProjectPose();
    return true;
  }
  return false;
}
// #endregion

// #region RENDER POSE API CALLS
export async function renderPose(
  projectID: string,
  prompt: string,
  imageBase64: string,
) {
  if (!projectStore.state.currentProject.data) {
    noticesStore.info(
      'A data error occurred, please refresh the page and try again.',
    );
    return;
  }
  const jobIDs = projectStore.getJobIDsFromProjectTimeline(
    projectStore.state.currentProject.data,
  );
  if (jobIDs.length === 0) {
    noticesStore.info('Change the pose before doing your first render.');
    return;
  }
  const activeCharacterID = characterPickerStore.state.activeCharacter?.PK;

  state.renderingInProgress = true;
  state.lastRenderPrompt = prompt;

  // Step 1: Prepare the render (calls Gemini labeling and saves to S3)
  const prepareResponse = await postPrepareRenderCanvas(projectID, {
    characterID: activeCharacterID || '',
    prompt,
    imageBase64,
  });

  if (prepareResponse.error) {
    await handleGenericErrors(prepareResponse.error);
    state.renderedPoseURL = undefined;
    state.renderingInProgress = false;
    return;
  }

  // Step 2: Perform the actual rendering (loads from S3, calls Gemini rendering)
  const response = await postRenderCanvas(projectID, { prompt });

  if (response.error) {
    await handleGenericErrors(response.error);
    state.renderedPoseURL = undefined;
    state.renderingInProgress = false;
    return;
  }

  if (response.data?.url) {
    state.renderedPoseURL = response.data.url;
    state.renderingInProgress = false;
  }

  if (state.posingSystem?.current?.poser) {
    try {
      const poser = state.posingSystem.current.poser;

      const bvhContent = poser.exportBVHPose();

      // We intentionally get a new canvasBase64 image here, because the one sent to renderPose is a gemini-friendly version, but we don't
      // want the gemini version to end up on the pose.
      const canvasImageTaskURL =
        await state.posingSystem.current.canvasImageTask();
      const canvasBase64 = canvasImageTaskURL.replace(
        /^data:image\/\w+;base64,/,
        '',
      );
      await postPose(projectID, {
        bvhContent,
        canvasImageBase64: canvasBase64,
        title: '',
        duration: 1,
      });

      await projectStore.loadCurrentProject(projectID, false);
    } catch (error) {
      // Fallback to a more complete default pose if generation fails
    }
  }

  return response;
}

export async function setFeedback(
  sentiment: 'positive' | 'negative',
  comment?: string,
) {
  const project = projectStore.state.currentProject;

  if (!project?.data?.projectID) {
    noticesStore.info(
      'Could not submit your feedback. Please refresh the page and try again',
    );
    return;
  }
  const response = await postRenderCanvasFeedback(project.data.projectID, {
    sentiment,
    comment,
  });
  if (response?.error) {
    await handleGenericErrors(response.error);
    return;
  }
  return response;
}
// #endregion
